// Words no new column, metric, metric group or attribute may be called, in any
// capitalisation. Each is a key the platform writes into its own records and recognises by
// its presence, so a user's column with the same name is mistaken for the platform's
// bookkeeping: `period` is a time-series table's reporting-period key; `type` and
// `updateBehavior` mark a metric column in a table's metric schema; `mandatory` is an
// attribute column's upload-tag requirement, which create_table refuses on a detail column or
// metric leaf (those declare mandatory_per_file and mandatory_per_unit); `add`, `before` and
// `after` mark one changed value in a unit's diff.
// Backend twin: common/column_name_validation.py RESERVED_PLATFORM_KEYWORDS.
export const RESERVED_PLATFORM_KEYWORDS = [
  'period',
  'type',
  'updateBehavior',
  'mandatory',
  'add',
  'before',
  'after',
];

// `noun` is what the person is naming: a 'column' in the table wizard, an 'attribute' where
// tag categories are created (a category's id becomes an attribute column's name).
export function reservedKeywordError(name, noun) {
  const trimmed = String(name ?? '').trim();
  const isReserved = RESERVED_PLATFORM_KEYWORDS.some(
    keyword => keyword.toLowerCase() === trimmed.toLowerCase()
  );
  if (!isReserved) return null;
  return `"${trimmed}" is a reserved platform keyword. Please rename this ${noun}.`;
}

// Creation accepts human-readable names. Only the backend derives stored keys.
export function columnNameError(name) {
  if (typeof name !== 'string' || !name.trim()) return 'Every column needs a name.';
  if (name.includes('_')) return `Name "${name}" cannot contain underscores. Use spaces instead.`;
  if (!/^[A-Za-z][A-Za-z0-9 ]*$/.test(name.trim())) {
    return `Name "${name}" is invalid. Use letters, numbers and spaces, starting with a letter.`;
  }
  return reservedKeywordError(name, 'column');
}

export function tableCreationNamesError(table) {
  const metricSchema = table.metricColumns?.schema || {};
  const topNames = [
    table.identifier?.name,
    ...(table.detailColumns || []).map(column => column.name),
    ...(table.attributeColumns || []).map(column => column.name),
    ...(table.tableAttributeColumns || []).map(column => column.name),
    ...Object.keys(metricSchema),
  ];
  if (!table.identifier?.name) topNames.shift();
  const scopes = [['table', topNames]];
  Object.entries(metricSchema).forEach(([name, config]) => {
    if (!config) return;
    // A metric column carries its type as text under `type`; a group holds named metric
    // columns instead. An object under `type` is therefore a group with a column called
    // "type", whose name the keyword check refuses.
    const typeIsAnObject = config.type !== null && typeof config.type === 'object' && !Array.isArray(config.type);
    const isGroup = !Object.prototype.hasOwnProperty.call(config, 'type') || typeIsAnObject;
    if (isGroup) scopes.push([`group "${name}"`, Object.keys(config)]);
  });
  for (const [scope, names] of scopes) {
    const seen = new Map();
    for (const name of names) {
      const error = columnNameError(name);
      if (error) return error;
      const key = name.trim().replace(/ /g, '_').toLowerCase();
      if (seen.has(key)) return `Duplicate column names "${seen.get(key)}" and "${name}" in ${scope}.`;
      seen.set(key, name);
    }
  }
  return null;
}

// Catalog templates contain stored identifiers. Convert those to the same
// editable labels a person would type, without changing any business values.
export function metricNamesForEditing(schema) {
  const editable = {};
  Object.entries(schema || {}).forEach(([name, config]) => {
    const label = name.replace(/_/g, ' ');
    if (Object.prototype.hasOwnProperty.call(editable, label)) {
      throw new Error(`Duplicate metric name "${label}" in the template.`);
    }
    editable[label] = Object.prototype.hasOwnProperty.call(config, 'type')
      ? { ...config }
      : metricNamesForEditing(config);
  });
  return editable;
}
